import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import TextInput from "../../Components/InputComponents/TextInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const DeployedClientCandidateList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const candidateId =
    sessionStorage?.getItem(`SWA_UserId`) ||
    localStorage?.getItem(`SWA_UserId`);

  const [candidateData, setcandidateData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loaderOpen, setLoaderOpen] = useState(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
    setPage(0);
  };

  const [filterData, setFilterData] = useState({
    name: "",
    profession: "",
    jobapplication: "",
    jobid: "",
    interviewdate: "",
  });

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
    from: `DeployedCandidates`,
  };

  const handleResetFilters = () => {
    const resetFilters = {
      name: "",
      profession: "",
      jobapplication: "",
      jobid: "",
      interviewdate: "",
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }

    navigate(`/Candidates`, { replace: true });
  };

  const navigatetoView = (id) => {
    navigate(`/Candidates/View`, {
      state: {
        ...paginationState,
        selectedId: JSON.stringify(id),
      },
    });
  };

  const [professionList, setProfessionList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [jobList, setJobList] = useState([]);

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatus = async () => {
    try {
      const res = await axiosAPI.get(`/JobApplicationStatus/list`);
      setStatusList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const clientId =
    sessionStorage?.getItem(`SWA_UserId`) ||
    localStorage?.getItem(`SWA_UserId`);

  const getJobs = async () => {
    const trimmedFilters = {
      JD_Title: ``,
      JD_Country: ``,
      JD_Qualification: ``,
      JD_Profession: ``,
      JD_Status: ``,
    };

    try {
      const res = await axiosAPI?.post(
        `/clientportal/joblist/${clientId}`,
        trimmedFilters
      );
      setJobList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = (filter = filterData) => {
    const trimmedFilters = {
      CI_Name: filter?.name, // name
      CI_Profession: filter?.profession, // profession //
      JASL_Id: filter?.jobapplication, // jpb application status //
      Jobid: filter?.jobid, // job list
      JA_InterviewDate: filter?.interviewdate, // date
    };

    axiosAPI
      ?.post(`/clientportal/deployedCandidatelist/${candidateId}`, trimmedFilters)

      .then((res) => {
        setcandidateData(res?.data?.data);
        setLoaderOpen(false);


      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getProfession();
    getJobs();
    getStatus();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (location?.state?.pagination) {
      setPage(location.state.pagination.page || 0);
      setRowsPerPage(location.state.pagination.rowsPerPage || 25);
    }

    if (location?.state?.filterData) {
      setFilterData(location.state.filterData);
      getData(location.state.filterData); // fetch with restored filters
    } else {
      getData();
    }
  }, [location?.state, page, rowsPerPage]);

  const isMobile = useMediaQuery("(max-width: 1220px)");

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Deployed Candidates
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Name
            </Typography>
            <TextField
              placeholder="Name"
              variant="outlined"
              size="small"
              value={filterData.name}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/* Profession */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Profession
            </Typography>
            <SelectInput
              value={filterData?.profession}
              name="profession"
              onChange={handleFilterChange}
              options={professionList?.map((item) => ({
                value: item?.PI_Id,
                label: item?.PI_Name,
              }))}
              placeholder="Select Profession"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/*  Job Application */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job Application Status
            </Typography>
            <SelectInput
              value={filterData.jobapplication}
              name="jobapplication"
              onChange={handleFilterChange}
              options={statusList?.map((item) => ({
                value: item?.JASL_Id,
                label: item?.JASL_Name,
              }))}
              placeholder="Select Status"
            />
          </Grid>

          {/* Job */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job
            </Typography>
            <SelectInput
              value={filterData.jobid}
              name="jobid"
              onChange={handleFilterChange}
              options={jobList?.map((item) => ({
                value: item?.JD_Id,
                label: item?.JD_Title,
              }))}
              placeholder="Select Job"
            />
          </Grid>

          {/*  Interview Date */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Interview Date
            </Typography>
            <TextInput
              placeholder="Name"
              variant="outlined"
              size="small"
              type="date"
              value={filterData.interviewdate}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  interviewdate: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
             
              <StyledTableCell>Profession </StyledTableCell>
              <StyledTableCell>Applied Job</StyledTableCell>
              <StyledTableCell>Job Application Status </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "200px" }}>
                Actions{" "}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                  <StyledTableCell>
                    <Skeleton variant="rectangular" width="100%" height={40} />
                  </StyledTableCell>
                </TableRow>
              ))
            ) : candidateData && candidateData.length > 0 ? (
              candidateData?.map((parent, index) => (
                <StyledTableRow key={parent.id}>
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {(page + 1) * rowsPerPage - (rowsPerPage - (index + 1))}
                  </StyledTableCell>

                  {/* CI_Name */}
                  <StyledTableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar
                        src={
                          parent?.CI_Photo
                            ? `${process.env.REACT_APP_BASE_URL}/${parent.CI_Photo}`
                            : undefined
                        }
                        imgProps={{
                          onError: (e) => {
                            e.currentTarget.src = "";
                          },
                        }}
                        sx={{ width: 46, height: 46, mr: 1 }}
                      >
                        {parent?.CI_Name?.[0] || ""}
                      </Avatar>

                      <Typography
                        sx={{ fontFamily: "Geist, sans-serif" }}
                        variant="body2"
                      >
                        {parent?.CI_Name || "-"}
                      </Typography>
                    </Box>
                  </StyledTableCell>

               
                  {/* Applied_profession_Name		 */}
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent.Applied_profession_Name
                      ? parent.Applied_profession_Name
                      : "-"}
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent.job_applied ? parent.job_applied : "-"}
                  </StyledTableCell>

                  {/* JobApplication_status	 */}
                  <StyledTableCell
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    {parent.JobApplication_status
                      ? parent.JobApplication_status
                      : "-"}
                  </StyledTableCell>

                  {/* Actions */}
                  <StyledTableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => navigatetoView(parent)}
                    >
                      <Visibility />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={10} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={candidateData?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default DeployedClientCandidateList;
